import {
  Component,
  effect,
  ElementRef,
  forwardRef,
  HostListener,
  input,
  model,
  output, signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { NgClass } from "@angular/common";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

@Component({
  selector: 'select-dropdown',
  imports: [NgClass],
  providers: [{
    provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectDropdownComponent), multi: true
  }],
  templateUrl: './select-dropdown.component.html',
  styleUrl: './select-dropdown.component.scss'
})
export class SelectDropdownComponent implements ControlValueAccessor {
  container = viewChild<ElementRef>('container');

  // UI
  width = input<number>();
  widthPercent = input<number>();
  height = input<number | null>(null);
  placeholder = input<string>('Select item');
  disabled = model<boolean>(false);
  label = input<string>('');
  size = input<'sm' | 'md' | 'lg'>('lg');
  checkedValues = input<any[]>([]);
  closeOnSelect = input<boolean>(true);
  isOpen: WritableSignal<boolean> = signal(false);

  // DATA
  value = model<any>(null);
  items = input.required<DropdownItem[]>();
  onIndexSelected = output<number>();
  onItemSelected = output<any>();
  selected_item!: DropdownItem | null;

  constructor() {
    effect(() => this.selected_item = this.items().find(i => i.value === this.value()) || null);
  }

  // control value accessor functions
  private onChange = (val: any) => {};
  private onTouched = () => {};

  select(item: DropdownItem, index: number, event: Event) {
    event.stopPropagation();
    this.value.set(item.value);
    this.selected_item = item;
    this.onIndexSelected.emit(index);
    this.onItemSelected.emit(item);
    this.onChange(item.value);
    this.onTouched();
    if (this.closeOnSelect()) {
      this.isOpen.set(false);
    }
  }

  toggle(event: Event) {
    event.stopPropagation();
    if (this.disabled()) return;
    this.isOpen.update(el => !el);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const host = this.container()?.nativeElement as HTMLElement | undefined;
    const target = event.target as Node | null;
    if (!host || !target) {
      return;
    }

    if (!host.contains(target)) {
      this.isOpen.set(false);
    }
  }

  public clear() {
    this.selected_item = null;
  }

  protected isChecked(value: any): boolean {
    return this.checkedValues().includes(value);
  }

  writeValue(val: any): void {
    this.value.set(val);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}

export interface DropdownItem {
  name: string,
  value: any
}
